import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  HitsPerPage,
  Panel,
  CurrentRefinements,
  Configure,
  connectRefinementList,
} from 'react-instantsearch-dom';
import moment from 'moment/moment';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { debounce } from 'lodash';
import { Button, Modal, Spinner } from 'react-bootstrap';
import currency from 'currency.js';
import { SearchState } from 'react-instantsearch-core';
import {ALGOLIA_ALL_LISTINGS_INDEX, BE_STATES} from '../../../utils/constants';
import { ResultsNumberMobile, NoResults } from '../../search/widgets';
import getEmptyFilter from '../../search/widgets/filters/empty-filter';
import getSelectDropdown from '../../search/widgets/filters/select-dropdown';
import SelectWidget from '../../search/widgets/SelectWidget';
import useRequestLocation from '../../../hooks/useRequestLocation';
import SetLocation from '../../buttons/set-location';
import CustomAgeFilter from '../../forms/Partials/CustomAgeFilter';
import CustomPriceFilter from '../../search/widgets/filters/CustomPriceFilter';
import ClearRefinements from '../../search/widgets/ClearRefinements';
import { FacetObject, PriceParams, QueryParams } from '../../search/admin-dashboard-tables/interfaces';
import CustomSortBy from '../../search/widgets/sorts/CustomSortBy';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import CustomRenderListingInfiniteHits from '../../search/widgets/CustomRenderListingInfiniteHits';
import ConnectedToggleSwitch from '../../search/widgets/filters/ConnectedToggleSwitch';
import PurebredHybridSearchCTA from '../../search/widgets/PurebredHybridSearchCTA';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import useScrollToRef from '../../../utils/hooks/useScrollToRef';

const DefaultSellerFilter = connectRefinementList(getEmptyFilter());
const DefaultListingsTypeFilter = connectRefinementList(getEmptyFilter());
const EmptyBreedFilter = connectRefinementList(getEmptyFilter());
const EmptyFilter = connectRefinementList(getEmptyFilter());
const DefaultAvailabilityFilter = connectRefinementList(getEmptyFilter());
const DefaultPublicationStatusFilter = connectRefinementList(getEmptyFilter());
const CustomBreedFilter = connectRefinementList(getSelectDropdown(true, 'Breed', [], '', true));
const CustomSizeFilter = connectRefinementList(getSelectDropdown(true, 'Size'));
const CustomColorFilter = connectRefinementList(getSelectDropdown(true, 'Color'));
const CustomSexFilter = connectRefinementList(getSelectDropdown(true, 'Sex'));
const CustomStateFilter = connectRefinementList(getSelectDropdown(true, 'State'));
const CustomCategoryFilter = connectRefinementList(getSelectDropdown(true, 'Category'));
const CustomRegistrationFilter = connectRefinementList(getSelectDropdown(true, 'Registration'));
const CustomGenerationFilter = connectRefinementList(getSelectDropdown(true, 'Generation'));

interface SearchResultsBlockProps extends RouteComponentProps {
  breedSlug?: string;
  sellerSlug?: string;
  categorySlug?: any;
  filterByState?: string;
  listingsType: string,
  currentSort: string,
  setCurrentSort: (sort: string) => void,
  showNonPublished?: boolean,
  onlyOnSale: string,
  underFiveHundred: string,
  availableToday: string,
  newArrivals: string,
  searchState: SearchState,
  setSearchState: (args: any) => void,
}

// Since expires_at only updates on page load, there is a chance that expired
// listings could be returned if a page is open for a long time.
// The trade-off, however, is that we perform fewer queries,
// since InstantSearch can cache the initial query.
const defaultNumericFilters = [[
  `expires_at > ${moment().utc().format('X')}`,
  'can_expire = 0',
]];

const availableTodayForAlgolia = moment()
  .utc()
  .endOf('day')
  .format('X');

const newArrivalsForAlgolia = moment()
  .utc()
  .subtract(7, 'days')
  .format('X');

const SearchResultsBlock: React.FC<SearchResultsBlockProps> = (props): ReactElement => {
  const {
    breedSlug,
    sellerSlug,
    filterByState,
    listingsType,
    showNonPublished,
    searchState,
    setSearchState,
    currentSort,
    setCurrentSort,
    categorySlug,
    onlyOnSale,
    underFiveHundred,
    availableToday,
    newArrivals,
  } = props;

  const {
    searchLocation,
    showRequestLocation,
    useSearchByLocation,
    closeModal,
    locationIsLoading,
    setLocationIsLoading,
    stopAsking,
    isAuto,
    onCompletionCallback,
    toggleSearchByLocation,
  } = useRequestLocation(30000);

  const fetchInitialMinPrice = () => {
    const { minPrice }: PriceParams = fetchSearchParams();
    if (minPrice && Number(minPrice)) {
      return currency(minPrice, { fromCents: true }).value.toString();
    }
    return undefined;
  };

  const fetchInitialMaxPrice = () => {
    const { maxPrice }: PriceParams = fetchSearchParams();
    if (maxPrice && Number(maxPrice)) {
      return currency(maxPrice, { fromCents: true }).value.toString();
    }
    return undefined;
  };

  const [numericFilters, setNumericFilters] = useState<Array<string | Array<string>>>(
    defaultNumericFilters,
  );
  const [minPrice, setMinPrice] = useState<string | undefined>(fetchInitialMinPrice());
  const [maxPrice, setMaxPrice] = useState<string | undefined>(fetchInitialMaxPrice());
  const [selectedAgeValue, setSelectedAgeValue] = useState<Array<FacetObject>>([]);
  const [defaultSortRefinement] = useState<string>(ALGOLIA_ALL_LISTINGS_INDEX!);
  const [currentPage, setCurrentPage] = useState(searchState.page || 1);
  const [searchHybrids, setSearchHybrids] = useState(() => {
    const { admin_single_breed }: QueryParams = fetchSearchParams();
    return admin_single_breed === 'false';
  });

  const updateAgeQuery = useCallback((range: { min: string | number, max: string | number }) => {
    const { min, max } = range;
    if (!min && !max) {
      setNumericFilters([
        ...numericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('birth_date');
        }),
      ]);
    } else {
      setNumericFilters([
        ...numericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('birth_date');
        }),
        `birth_date:${min} TO ${max}`,
      ]);
    }
  }, [numericFilters, setNumericFilters]);

  useEffect(() => {
    if (selectedAgeValue.length > 0) {
      const { value } = selectedAgeValue[0];
      if (value) {
        let newMin = '';
        let newMax = moment().utc().format('X');
        const numVal = parseInt(value, 10);
        if (numVal > 1) {
          newMin = moment().utc().subtract(numVal, 'months').format('X');
        } else {
          newMax = moment().utc().subtract(numVal, 'years').format('X');
        }

        updateAgeQuery({
          min: newMin ? parseInt(newMin, 10) : 0,
          max: parseInt(newMax, 10),
        });
      }
    } else {
      updateAgeQuery({
        min: '',
        max: '',
      });
    }
  }, [selectedAgeValue]);

  const updatePriceQuery = useCallback((min?: string | number, max?: string | number) => {
    if (min && !max) {
      setNumericFilters([
        ...numericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
        `real_price >= ${min}`,
      ]);
    } else if (max && !min) {
      setNumericFilters([
        ...numericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
        `real_price <= ${max}`,
      ]);
    } else if (!min && !max) {
      setNumericFilters([
        ...numericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
      ]);
    } else {
      setNumericFilters([
        ...numericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
        `real_price:${min} TO ${max}`,
      ]);
    }
  }, [numericFilters, setNumericFilters]);

  const debouncePriceUpdate = useRef(
    debounce((min, max) => updatePriceQuery(min, max), 500),
  ).current;

  useEffect(() => {
    return () => {
      debouncePriceUpdate.cancel();
    };
  }, [debouncePriceUpdate]);

  useEffect(() => {
    const newMin = minPrice ? currency(minPrice).intValue : '';
    const newMax = maxPrice ? currency(maxPrice).intValue : '';
    debouncePriceUpdate(newMin, newMax);
  }, [debouncePriceUpdate, minPrice, maxPrice]);

  useEffect(() => {
    // Set initial page sort, if not default
    if (newArrivals) {
      // Sort by most recently posted
      setCurrentSort(`${ALGOLIA_ALL_LISTINGS_INDEX}_created_at_desc`);
    }
  }, []);

  const breedRefinement = breedSlug ? { defaultRefinement: [breedSlug] } : {};
  const sellerRefinement = sellerSlug ? { defaultRefinement: [sellerSlug] } : {};
  const categoryRefinement = categorySlug ? { defaultRefinement: [categorySlug] } : {};
  const stateRefinement = filterByState ? { defaultRefinement: [filterByState] } : {};
  const listingsTypeRefinement = listingsType ? { defaultRefinement: [listingsType] } : {};
  const publicationStatusRefinement = showNonPublished ? {} : { defaultRefinement: ['published'] };
  const availabilityRefinement = { defaultRefinement: ['available', 'pending'] };

  const ignoredRefinements = [
    ...sellerSlug ? ['seller_id'] : [],
    ...breedSlug ? ['admin_breed_parents'] : [],
    ...categorySlug ? ['category'] : [],
    ...filterByState ? ['state'] : [],
    ...listingsType ? ['listings_type'] : [],
    ...showNonPublished ? [] : ['status'],
    ...underFiveHundred ? ['real_price'] : [],
    'status',
    'availability',
    'admin_single_breed',
  ];

  const filterRefinements = (item) => {
    return !ignoredRefinements.includes(item.attribute);
  };

  const numericFiltersByRoute = () => {
    const filters: string[] = [];

    if (underFiveHundred === 'true') {
      filters.push('real_price <= 50000');
    }

    if (onlyOnSale === 'true') {
      filters.push('sale_price > 0');
    }

    if (availableToday === 'true') {
      filters.push(`date_available <= ${availableTodayForAlgolia}`);
    }

    if (newArrivals === 'true') {
      filters.push(`created_at > ${newArrivalsForAlgolia}`);
    }

    return filters;
  };

  // NB: Since these values are all tracked manually, and are reset separately from the connected
  // facets, resetting them may cause an additional query to be consumed.
  // This issue is mitigated by InstantSearch caching of initial state query.
  // E.g. if `admin_breed_parents` and `min_price` are both set, two separate queries will be made
  // to return to initial state.
  const onReset = useCallback(() => {
    setNumericFilters(defaultNumericFilters);

    if (minPrice) {
      setMinPrice(undefined);
    }
    if (maxPrice) {
      setMaxPrice(undefined);
    }

    if (selectedAgeValue.length > 0) {
      setSelectedAgeValue([]);
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [minPrice, maxPrice, selectedAgeValue, currentPage]);

  const resultsContainerRef = useRef<HTMLElement>(null);
  const searchControlsRef = useRef<HTMLElement>(null);

  // When on mobile, "scroll to top" should take you to the top of search results (since the
  // controls take up the entire page). On desktop, the scroll should include the controls
  const { width } = useWindowDimensions();
  const scrollRef = width > 768 ? searchControlsRef : resultsContainerRef;
  const scrollToComponentTop = useScrollToRef(scrollRef);

  const onCTAToggleSearchType = () => {
    setSearchHybrids((val) => !val);
    scrollToComponentTop();
  };

  const customSortByCallback = (value: string) => {
    if (value === 'location') {
      toggleSearchByLocation(true);
    } else {
      toggleSearchByLocation(false);
    }
    setCurrentSort(value);
  };

  // If the user does not set location, sort by default instead
  const handleLocationDenial = () => {
    closeModal();
    setCurrentSort(defaultSortRefinement);
  };

  const onAutoCallback = useCallback(() => {
    if (isAuto) {
      setCurrentSort('location');
    }
  }, [isAuto]);

  const handleSetLocationCompletion = useCallback(() => {
    onCompletionCallback(true);
    onAutoCallback();
  }, [onCompletionCallback, onAutoCallback]);

  const searchTitle = `${
    breedSlug ? `${breedSlug} ` : ''
  }${
    searchHybrids ? 'Hybrid' : 'Purebred'
  } ${
    listingsType === 'puppy' ? 'Puppies' : 'Studs'
  } for Sale`;

  // The CTA component shows the opposite of what the main search shows, which is why
  // Purebred & Hybrid are flipped
  const ctaTitle = `View ${
    breedSlug ? `${breedSlug} ` : ''
  }${
    searchHybrids ? 'Purebred' : 'Hybrid'
  } ${
    listingsType === 'puppy' ? 'Puppies' : 'Studs'
  } for Sale`;

  return (
    <>
      <section className="results-container" ref={searchControlsRef}>
        <h2 className="search-title">{searchTitle}</h2>
        <div
          className="container-filters d-flex flex-column flex-xl-column-reverse justify-content-between align-items-stretch align-items-xl-start"
        >
          <div
            className="d-flex flex-wrap flex-xl-nowrap align-self-xl-end justify-content-xl-between w-100 pb-2 pb-xl-0 stats-and-sort-container"
          >
            <div className="mr-3 mb-1 text-nowrap">
              <ResultsNumberMobile />
            </div>
            <div className="d-flex flex-wrap flex-xl-nowrap mb-1">
              <SelectWidget
                label="Show:"
                className="mr-3 align-items-center"
              >
                <HitsPerPage
                  className="container-option"
                  items={[
                    {
                      label: '50',
                      value: 50,
                    },
                    {
                      label: '100',
                      value: 100,
                    },
                    {
                      label: '200',
                      value: 200,
                    },
                    {
                      label: '300',
                      value: 300,
                    },
                    {
                      label: '400',
                      value: 400,
                    },
                  ]}
                  defaultRefinement={200}
                />
              </SelectWidget>
              <SelectWidget
                label="Sort By:"
                className="mr-3 align-items-center"
              >
                <CustomSortBy
                  wrapperClass="container-option"
                  onChangeCallback={customSortByCallback}
                  defaultRefinement={defaultSortRefinement}
                  selected={currentSort}
                  items={[
                    {
                      label: 'Relevance',
                      value: defaultSortRefinement,
                    },
                    {
                      label: 'Location',
                      value: 'location',
                    },
                    {
                      label: 'Most Recently Listed',
                      value: `${ALGOLIA_ALL_LISTINGS_INDEX}_created_at_desc`,
                    },
                    {
                      label: 'Youngest',
                      value: `${ALGOLIA_ALL_LISTINGS_INDEX}_birth_date_desc`,
                    },
                    {
                      label: 'Most Recently Updated',
                      value: `${ALGOLIA_ALL_LISTINGS_INDEX}_updated_at_desc`,
                    },
                    {
                      label: 'Lowest Price',
                      value: `${ALGOLIA_ALL_LISTINGS_INDEX}_real_price_asc`,
                    },
                    {
                      label: 'Highest Price',
                      value: `${ALGOLIA_ALL_LISTINGS_INDEX}_real_price_desc`,
                    },
                  ]}
                />
              </SelectWidget>
            </div>
          </div>
          <div className="d-flex flex-column">
            <Panel className="py-2">
              <ConnectedToggleSwitch
                labelLeft="Purebred / Designer"
                labelRight="Hybrid / Mixed Breed"
                labelLeftClass="toggle toggle-left px-3"
                labelRightClass="toggle toggle-right px-3"
                containerClass="purebred-hybrid-toggle"
                id="admin_single_breed"
                attribute="admin_single_breed"
                checked={searchHybrids}
                setChecked={setSearchHybrids}
                defaultRefinement={searchHybrids ? ['false'] : ['true']}
                flipToggleDirection
              />
            </Panel>
            <div className="d-flex flex-wrap">
              {sellerSlug && (
                <DefaultSellerFilter
                  attribute="seller_id"
                  {...sellerRefinement}
                />
              )}
              {listingsType && (
                <DefaultListingsTypeFilter
                  attribute="listings_type"
                  {...listingsTypeRefinement}
                />
              )}
              <DefaultAvailabilityFilter
                attribute="availability"
                {...availabilityRefinement}
              />
              {!showNonPublished && (
                <DefaultPublicationStatusFilter
                  attribute="status"
                  {...publicationStatusRefinement}
                />
              )}
              {
                breedSlug
                  ? <EmptyBreedFilter attribute="admin_breed_parents" {...breedRefinement} />
                  : (
                    <Panel>
                      <CustomBreedFilter limit={1000} attribute="admin_breed_parents" {...breedRefinement} />
                    </Panel>
                  )
              }
              <Panel>
                <CustomSizeFilter attribute="size" />
              </Panel>
              <Panel>
                <CustomColorFilter limit={1000} attribute="colors" />
              </Panel>
              <Panel>
                <CustomSexFilter attribute="sex" />
              </Panel>
              <Panel>
                <CustomAgeFilter
                  options={[
                    {
                      value: '3',
                      label: 'Less than three months',
                    },
                    {
                      value: '6',
                      label: 'Less than six months',
                    },
                    {
                      value: '9',
                      label: 'Less than nine months',
                    },
                    {
                      value: '12',
                      label: 'Less than a year',
                    },
                    {
                      value: '1',
                      label: 'More than a year',
                    },
                  ]}
                  placeholder="Age"
                  label="Age"
                  selectedValue={selectedAgeValue}
                  setSelectedValue={setSelectedAgeValue}
                />
              </Panel>
              <Panel>
                {
                  filterByState
                    ? <EmptyFilter attribute="state" {...stateRefinement} />
                    : <CustomStateFilter attribute="state" />

                }
              </Panel>
              <Panel>
                {
                  categorySlug
                    ? <EmptyFilter attribute="category" {...categoryRefinement} />
                    : (
                      <CustomCategoryFilter attribute="category" />
                    )
                }
              </Panel>
              <Panel>
                <CustomRegistrationFilter attribute="registrations" />
              </Panel>
              <Panel>
                <CustomGenerationFilter attribute="generation" />
              </Panel>
              <Panel>
                {
                  underFiveHundred
                    ? <EmptyFilter attribute="real_price" />
                    : (
                      <CustomPriceFilter
                        min={minPrice || ''}
                        max={maxPrice || ''}
                        setMin={setMinPrice}
                        setMax={setMaxPrice}
                      />
                    )
                }
              </Panel>
              <div className="clear-filters" data-layout="desktop">
                <ClearRefinements
                  // @ts-ignore
                  onReset={onReset}
                  className="clear-filters-button"
                  label="Clear Filters"
                  transformItems={(items) => items.filter(filterRefinements)}
                  translations={{
                    reset: (
                      <>
                        Clear Filters
                      </>
                    ),
                  }}
                  disableIfEmpty={
                    (!minPrice && !maxPrice && selectedAgeValue.length === 0)
                    || !!underFiveHundred
                  }
                />
              </div>
            </div>
            <CurrentRefinements
              transformItems={(items) => {
                // Set label to breed: instead of admin_breed_parents:
                const itemsEdited = items.map((item) => {
                  const retItem = item;
                  if (retItem.attribute === 'admin_breed_parents') {
                    retItem.label = 'breed:';
                  }

                  return retItem;
                });

                return itemsEdited.filter(filterRefinements);
              }}
            />
          </div>
        </div>
        {/* We must add age separately, since it's technically a numericFilter, */}
        {/* not a refinement */}
        {selectedAgeValue.length > 0 && (
          <div className="ais-CurrentRefinements">
            <ul className="ais-CurrentRefinements-list">
              <li className="ais-CurrentRefinements-item">
                <span className="ais-CurrentRefinements-label">
                  Age:
                </span>
                <span className="ais-CurrentRefinements-category">
                  <span className="ais-CurrentRefinements-categoryLabel">
                    {selectedAgeValue[0].label}
                  </span>
                  <button
                    className="ais-CurrentRefinements-delete"
                    onClick={() => setSelectedAgeValue([])}
                  >
                    ✕
                  </button>
                </span>
              </li>
            </ul>
          </div>
        )}
        <Configure
          attributesToSnippet={['description:2']}
          snippetEllipsisText="…"
          removeWordsIfNoResults="allOptional"
          facetFilters={[`listings_type:${listingsType}`]}
          filters={BE_STATES.map((state) => `state:${state}`).join(' OR ')}
          numericFilters={[
            ...numericFiltersByRoute(),
            ...numericFilters,
          ]}
          query={searchState.query}
          page={currentPage}
          aroundRadius="all"
          aroundLatLng={useSearchByLocation ? searchLocation : ''}
        />
        <main className="search-results-container" ref={resultsContainerRef}>
          <section className="container-results">
            <CustomRenderListingInfiniteHits
              breedSlug={breedSlug}
              hybrid={searchState.refinementList?.admin_single_breed
                && searchState.refinementList?.admin_single_breed[0] === 'false'}
              listingsType={listingsType}
              setSearchState={setSearchState}
            />
            <NoResults />
          </section>
        </main>
      </section>
      <PurebredHybridSearchCTA
        count={5}
        title={ctaTitle}
        listingsType={listingsType}
        searchState={searchState}
        selectedAgeValue={selectedAgeValue}
        searchIsHybrid={searchHybrids}
        toggleSearchType={onCTAToggleSearchType}
        externalBreedRefinement={breedSlug}
        externalSellerRefinement={sellerSlug
          ? parseInt(sellerSlug, 10)
          : undefined}
      />
      <Modal
        show={showRequestLocation}
        onHide={handleLocationDenial}
      >
        {
          locationIsLoading && (
            <div className="loading-overlay">
              <Spinner animation="border" variant="primary" />
            </div>
          )
        }
        <Modal.Header closeButton>
          <Modal.Title>Search By Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Would you like to search by location?</p>
          <SetLocation
            onInitiateSearch={() => setLocationIsLoading(true)}
            onCompletionCallback={handleSetLocationCompletion}
            showStaticForm
          />
        </Modal.Body>
        <Modal.Footer>
          {isAuto && (
            <Button
              variant="danger"
              onClick={() => stopAsking()}
            >
              Don't Ask Again
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleLocationDenial}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withRouter(SearchResultsBlock);
